import { Funnel, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { GUTTER_WIDTH, gridHeadCellClass } from '@/components/data-table/grid-styles'
import type {
  ColumnFilter,
  ColumnFilters,
  FilterFieldKind,
} from '@/components/filters/column-filter'
import {
  draftFromFilter,
  EMPTY_ROW_DRAFT,
  FILTER_ROW_DEBOUNCE_MS,
  filterFromDraft,
  filterSummary,
  rowFilterKind,
  type RowFilterDraft,
} from '@/components/filters/column-filter-row'
import { DateInput } from '@/components/filters/DateInput'
import { Input } from '@/components/ui/input'
import { messages } from '@/lib/messages'
import { cn } from '@/lib/utils'

export interface FilterRowColumn {
  id: string
  label: string
  kind: FilterFieldKind | undefined
}

interface FilterRowProps {
  columns: FilterRowColumn[]
  filters: ColumnFilters
  onChange: (columnId: string, filter: ColumnFilter | undefined) => void
}

/**
 * Başlık altı filtre satırı (uiarc veri ızgarası): metin sütununda "içerir", sayı/tarihte aralık.
 * Başlık menüsüyle aynı `columnFilters` durumunu yazar. `DataTable` ikinci `thead` satırı olarak çizer;
 * hücreler `<td>` (sütun başlığı değil), böylece başlık erişilebilirlik ağacı bozulmaz.
 */
export function FilterRow({ columns, filters, onChange }: FilterRowProps) {
  return (
    <tr>
      <td
        className={cn(
          gridHeadCellClass,
          'text-text-muted sticky left-0 z-[3] px-0 text-center',
          'shadow-[inset_-1px_0_0_var(--border)]',
        )}
        style={{ width: GUTTER_WIDTH }}
        aria-hidden="true"
      >
        <Funnel className="mx-auto size-3.5" />
      </td>
      {columns.map((column) => (
        <FilterRowCell
          key={column.id}
          column={column}
          filter={filters[column.id]}
          onApply={(filter) => onChange(column.id, filter)}
        />
      ))}
    </tr>
  )
}

const asDraft = (value: RowFilterDraft | { chip: true }): RowFilterDraft =>
  'chip' in value ? EMPTY_ROW_DRAFT : value

interface FilterRowCellProps {
  column: FilterRowColumn
  filter: ColumnFilter | undefined
  onApply: (filter: ColumnFilter | undefined) => void
}

function FilterRowCell({ column, filter, onApply }: FilterRowCellProps) {
  const { kind, label } = column
  const mode = rowFilterKind(kind)
  const filterKey = JSON.stringify(filter ?? null)
  const chip = filter !== undefined && 'chip' in draftFromFilter(filter, kind)

  const [draft, setDraft] = useState<RowFilterDraft>(() => asDraft(draftFromFilter(filter, kind)))
  // Son bilinen eşzamanlı filtre (uygulanan ya da bizim yazdığımız): dışarıdan farklı bir filtre
  // gelirse (başlık menüsü, Temizle, adres) taslak onunla yeniden kurulur; kendi yankımız taslağı bozmaz.
  const [syncedKey, setSyncedKey] = useState(filterKey)
  const [seenKey, setSeenKey] = useState(filterKey)
  if (seenKey !== filterKey) {
    setSeenKey(filterKey)
    if (filterKey !== syncedKey) {
      setSyncedKey(filterKey)
      setDraft(asDraft(draftFromFilter(filter, kind)))
    }
  }
  const [focusOnMount, setFocusOnMount] = useState(false)

  const onApplyRef = useRef(onApply)
  useEffect(() => {
    onApplyRef.current = onApply
  })
  useEffect(() => {
    if (chip || mode === 'none') return
    const next = filterFromDraft(draft, kind)
    const nextKey = JSON.stringify(next ?? null)
    if (nextKey === filterKey) return
    const timer = setTimeout(() => {
      setSyncedKey(nextKey)
      onApplyRef.current(next)
    }, FILTER_ROW_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [draft, filterKey, chip, mode, kind])

  const cellClass = cn(gridHeadCellClass, 'p-1')
  if (mode === 'none' && !chip) return <td className={cellClass} />

  if (chip && filter) {
    const summary = filterSummary(filter)
    return (
      <td className={cellClass}>
        <button
          type="button"
          title={messages.grid.filterRow.edit(label, summary)}
          aria-label={messages.filters.column.remove(label)}
          onClick={() => {
            setFocusOnMount(true)
            onApply(undefined)
          }}
          className="bg-brand-subtle text-foreground hover:bg-surface-muted ease-standard rounded-control flex h-8 w-full cursor-pointer items-center justify-between gap-1 px-2.5 text-xs transition-colors duration-(--duration-fast) motion-reduce:transition-none"
        >
          <span className="min-w-0 truncate">{summary}</span>
          <X className="text-text-muted size-3.5 shrink-0" />
        </button>
      </td>
    )
  }

  const inputClass = 'h-8 px-2 text-xs'
  // Tarih düğmesi daha dar iç boşlukla: iki tarih bir hücreye sığsın (`15.10.26`, `Başlangıç`).
  const dateClass = 'h-8 px-1 text-xs'
  const numberClass =
    '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
  const t = messages.grid.filterRow
  if (mode === 'text') {
    return (
      <td className={cellClass}>
        <Input
          type="text"
          autoFocus={focusOnMount}
          onFocus={() => setFocusOnMount(false)}
          aria-label={t.contains(label)}
          placeholder={t.containsPlaceholder}
          className={inputClass}
          value={draft.text}
          onChange={(event) => setDraft({ ...draft, text: event.target.value })}
        />
      </td>
    )
  }

  if (kind !== 'numeric') {
    return (
      <td className={cellClass}>
        <div className="flex gap-1">
          <DateInput
            compact
            autoFocus={focusOnMount}
            onFocus={() => setFocusOnMount(false)}
            aria-label={t.from(label)}
            placeholder={t.fromDatePlaceholder}
            className={dateClass}
            value={draft.from}
            onChange={(from) => setDraft({ ...draft, from })}
          />
          <DateInput
            compact
            aria-label={t.to(label)}
            placeholder={t.toDatePlaceholder}
            className={dateClass}
            value={draft.to}
            onChange={(to) => setDraft({ ...draft, to })}
          />
        </div>
      </td>
    )
  }

  return (
    <td className={cellClass}>
      <div className="flex gap-1">
        <Input
          type="number"
          autoFocus={focusOnMount}
          onFocus={() => setFocusOnMount(false)}
          aria-label={t.from(label)}
          placeholder={t.minPlaceholder}
          className={cn(inputClass, numberClass)}
          value={draft.from}
          onChange={(event) => setDraft({ ...draft, from: event.target.value })}
        />
        <Input
          type="number"
          aria-label={t.to(label)}
          placeholder={t.maxPlaceholder}
          className={cn(inputClass, numberClass)}
          value={draft.to}
          onChange={(event) => setDraft({ ...draft, to: event.target.value })}
        />
      </div>
    </td>
  )
}
